/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FieldArray, Form, Formik } from "formik";
import Colors from "constants/Colors";
import { GlobalAuthContext, GlobalLoaderContext } from "store/context";
import { Layout, Text } from "components";
import { Col, Row } from "components/Grid";
import Icon from "components/Icon";
import Loader from "components/Loader";
import {
  GridColumnTitle,
  InputField,
  Label,
  SelectField,
} from "components/Form";
import { FitinsurAPI, MessageCenter } from "utils";
import {
  FieldGrid,
  ColIcon,
  TableHeader,
  TableCollum,
  Content,
  InlineLabel,
  LoadContainer,
  MobileLabel,
  RowFilterOrder,
  TableHead,
  TableRow,
  TableBody,
  Table,
  DialogIcon,
  RowFilterIcon,
  FilterSearchContainer,
  RowFilter,
  FilterColumn,
  CheckBoxColumn,
  ContentTextarea,
  ButtonContainerRow,
  ButtonContent,
  TooltipCard,
  TooltipBox,
  TooltipText
} from "./styles";
import { Button } from "components/index";
import { FilterContainer } from "../list2/styles";
import Checkbox from "components/Form/CheckBox";
import DialogAlertMessage from "components/DialogAlertMessage";
import MultiSelectField from "components/Form/MultiSelectField";
import CacheManager from "utils/src/cache-manager";
import MultipleExport from "./MultipleExport";
import useWindowScroll from "services/useWindowScroll";

export default function ManagementPage() {
  const cache = new CacheManager();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const history = useHistory();
  const location = useLocation();
  const formik = useRef();
  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [persons, setPersons] = useState([]);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [affectedEnquiries, setAffectedEnquiries] = useState([]);
  const [currentPerson, setCurrentPerson] = useState(null);
  const [showExport, setShowExport] = useState(false);

  const [tooltips, setTooltips] = useState([]);
  const region = authContext.region === 'en-GB' ? 'en' : 'en';

  let scrollUpdate = useWindowScroll();

  const initialValues = {
    filter: { shortName: "", fullName: "", hierarchy: "", parent: "", holding: "" },
  };

  const columns = [
    {
      title: "SHORT NAME",
      value: "shortName",
    },
    {
      title: "FULL NAME",
      value: "fullName",
    },
    {
      title: "HIERARCHY",
      value: "hierarchy",
    },
    {
      title: "PARENT",
      value: "parent",
    },
    {
      title: "HOLDING",
      value: "holding",
    },
    {
      title: "ACTION",
      key:'actions',
    },
  ];

  function resetFilters() {
    formik.current.resetForm(initialValues);
    setCurrentPage(1);
    setSortBy({ key: null, direction: null });
    handleFilter(initialValues.filter);
  }

  function handleSortList(key) {
    let direction = "asc";
    if (sortBy.key === key && sortBy.direction === "asc") direction = "desc";
    const sort = { key: key, direction: direction };

    setSortBy(sort);
    setCurrentPage(1);
    getPersons(1, sort, `${location.search}`);
  }

  const jsonToQueryString = (json) => {
    const keys = Object.keys(json).filter((key) => json[key]);
    return keys
      .map((key, index) => {
        const sufix = index + 1 === keys.length ? "" : "&";
        return (
          encodeURIComponent(key) + "=" + encodeURIComponent(json[key]) + sufix
        );
      })
      .join("");
  };

  const handleFilter = (filters) => {
    const formikFilters = formik.current.values.filter;
    const { ...rest } = formikFilters;

    const filterObject = { ...rest, ...filters };
    const queryString = jsonToQueryString(filterObject)
    const filterQueryString = `?${queryString}`;

    setCurrentPage(1);
    getPersons(1, {}, filterQueryString);

    if (queryString) {
      localStorage.setItem('management_filter', filterQueryString)
    } else {
      localStorage.removeItem('management_filter')
    }

    history.push(`management${filterQueryString}`);
  };

  const handleSetFilterFromParams = () => {
    if (location.search) {
      const query = new URLSearchParams(location.search);

      if (query.get("shortName")) {
        formik.current?.setFieldValue(
          "filter.shortName",
          query.get("shortName")
        );
      }

      if (query.get("fullName")) {
        formik.current?.setFieldValue("filter.fullName", query.get("fullName"));
      }

      if (query.get("hierarchy")) {
        let hierarchyIds = query
          .get('hierarchy')
          .split(',')

        formik.current?.setFieldValue("filter.hierarchy", hierarchyIds);
      }

      if (query.get("parent")) {
        formik.current?.setFieldValue("filter.parent", query.get("parent")
        );
      }
      if (query.get("holding")) {
        formik.current?.setFieldValue("filter.holding", query.get("holding")
        );
      }

    }
  };

  const handleExportPersons = async (page = 1, filterQueryString = '?') => {
    let exportList = sessionStorage.getItem('texel-export-list-report');
    let list = [];
    if (exportList) list = JSON.parse(sessionStorage.getItem('texel-export-list-report'))
    let verify = list.length > 0 ? list.filter(item => item.nameList == 'Person Report') : false;
    if (!verify || verify.length == 0) {
      loading.start();
      setTimeout(() => loading.stop(), 2000);
      const url = `${filterQueryString}&requestPage=${page}`;

      fitinsur.setAuthToken(authContext.token);
      const document = await fitinsur.exportPersons(url).then(x => x.json());
      loading.start();
      setTimeout(() => loading.stop(), 2000);
      if (document.idDocument) {
        list.push({ idDocument: document.idDocument, processing: true, nameList: 'Person Report' });
        sessionStorage.setItem('texel-export-list-report', JSON.stringify(list));
      }
    } else {
      mc.error("Please, wait person report be downloaded.");
    }
  }

  const handleExportSanctionsReport = async () => {
    let exportList = sessionStorage.getItem('texel-export-list-report');
    let list = [];
    if (exportList) list = JSON.parse(sessionStorage.getItem('texel-export-list-report'))
    let verify = list.length > 0 ? list.filter(item => item.nameList == 'Sanctions Report') : false;
    if (!verify || verify.length == 0) {
      loading.start();
      setTimeout(() => loading.stop(), 2000);
      fitinsur.setAuthToken(authContext.token);
      const document = await fitinsur.requestExportSanctionsReport().then(x => x.json());
      loading.start();
      setTimeout(() => loading.stop(), 2000);
      if (document.idDocument) {
        list.push({ idDocument: document.idDocument, processing: true, nameList: 'Sanctions Report' });
        sessionStorage.setItem('texel-export-list-report', JSON.stringify(list));
      }
    } else {
      mc.error("Please, wait sanctions report be downloaded.");
    }
  }

  const handleExportInsurerInfo = async (page = 1, filterQueryString = '?') => {
    let exportList = sessionStorage.getItem('texel-export-list-report');
    let list = [];
    if (exportList) list = JSON.parse(sessionStorage.getItem('texel-export-list-report'))
    let verify = list.length > 0 ? list.filter(item => item.nameList == 'Insurer Info') : false;
    if (!verify || verify.length == 0) {
      loading.start();
      setTimeout(() => loading.stop(), 2000);
      fitinsur.setAuthToken(authContext.token);
      const document = await fitinsur.exportInsurerInfo().then(x => x.json());
      loading.start();
      setTimeout(() => loading.stop(), 2000);
      if (document.idDocument) {
        list.push({ idDocument: document.idDocument, processing: true, nameList: 'Insurer Info' });
        sessionStorage.setItem('texel-export-list-report', JSON.stringify(list));
      }
    } else {
      mc.error("Please, wait insurer information report be downloaded.");
    }
  }

  const [showFilter, setShowFilter] = useState(false);

  const renderNewRegisterButton = () => {
    return (
      <Text
        primary
        type="button"
        id="btnSubmit"
        style={{ cursor: "pointer" }}
        onClick={() => history.push("/management/person")}
      >
        + NEW REGISTER
      </Text>
    );
  };

  async function getPersons(
    currentPage = 1,
    sort = {},
    filterQueryString = "?"
  ) {
    loading.start();
    try {
      const url = `${filterQueryString || "?"}&sortBy=${JSON.stringify(
        sort
      )}&currentPage=${currentPage}`;
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur
        .requestGetPersonsList(url)
        .then((response) => response.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details);
        loading.stop();
        return;
      }
      setPersons(response.ret_value);
      setTotalPages(response.pagination_control.totalPages);
      loading.stop();
    } catch (err) {
      console.log(err);
      loading.stop();
    }
  }

  function setNextPage() {
    if (currentPage === totalPages) return;
    setCurrentPage(currentPage + 1);
    getPersons(currentPage + 1, sortBy, `${location.search}`);
  }

  const handleGetPreviusFilter = () => {
    const filterQueryString = localStorage.getItem('management_filter');

    console.log('QUERYSTRING', filterQueryString);

    if (filterQueryString) {
      history.push(`management${filterQueryString}`)
      getPersons(1, sortBy, filterQueryString)

    } else {
      getPersons()
    }
  }

  useEffect(() => {
    if (location.search) {
      handleSetFilterFromParams();
      getPersons(1, sortBy, `${location.search}`);
    } else {
      handleGetPreviusFilter();
    }

    cache.getTooltip("DOMAINS", region, "357", [585, 586, 588, 587, 593, 581, 589, 590], authContext.token).then(msg => {
      let obj = [];
      msg.map(element => {
        obj[element.MODEL_ITEM_ID] = element.MSG_TEXT;
      })
      setTooltips(obj)
    }).catch(err => console.log("error", err));
  }, []);

  const getAffectedEnquiries = (idPerson) => {
    loading.start();
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestListEnquiries(idPerson).then(x => x.json())
      .then(resp => {
        loading.stop();
        if (resp.ret_code === 200) {
          setCurrentPerson(idPerson)
          setAffectedEnquiries(resp.ret_value);
          setAlertDialogOpen(true);
        }
      }).catch(() => {
        loading.stop();
        mc.error("There was an error while fetching your data");
      });
  }

  const disableUser = (idPerson) => {
    loading.start();
    fitinsur.setAuthToken(authContext.token);
    fitinsur.disableUser(idPerson).then(x => x.json())
      .then(resp => {
        loading.stop();
        if (resp.ret_code === 200) {
          setAlertDialogOpen(false);
          setCurrentPerson(null);
          getPersons(1, sortBy, `${location.search}`);
        }
      }).catch(() => {
        loading.stop();
        mc.error("There was an error while fetching your data");
      });
  }
  useEffect(() => {
    if(scrollUpdate && !(!persons || persons.length === 0 || currentPage === totalPages)){
      if(!loading.active){
        setNextPage()
      }
    }
  }, [scrollUpdate])

  function renderModalApproval() {
    return (
      <DialogAlertMessage icon='exclamation' title="Attention" size='16px' show={alertDialogOpen} setDialog={() => setAlertDialogOpen(false)}>
        <ContentTextarea>
          {affectedEnquiries.length ?
            <>
              <Text>
                You cannot disable this person since it's being used in the below deals:
              </Text>
              <div style={{ maxHeight: 250, overflow: 'auto' }}>
                {affectedEnquiries.map((enquiry, index) => index + 1 <= 7 ? <p>{enquiry}</p> : <></>)}
              </div>
              <ButtonContainerRow>
                <ButtonContent><Button secundary onClick={() => setAlertDialogOpen(false)}>CANCEL</Button></ButtonContent>
              </ButtonContainerRow>
            </>
            :
            <>
              <Text>
                This action will disable this person and all other persons and relationships under it,
                this includes Employees, Legal Entities, etc. Do you want to proceed?
              </Text>
              <ButtonContainerRow>
                <ButtonContent><Button secundary onClick={() => setAlertDialogOpen(false)}>CANCEL</Button></ButtonContent>
                <ButtonContent>
                  <Button
                    style={{ backgroundColor: Colors.BLUE, color: Colors.WHITE, marginLeft: 15 }}
                    onClick={() => disableUser(currentPerson)}>
                    CONFIRM
                  </Button>
                </ButtonContent>
              </ButtonContainerRow>
            </>
          }
        </ContentTextarea>
      </DialogAlertMessage>
    )
  }

  const hierarchyOptions = [
    { label: "Holding", value: "holding" },
    { label: "Legal Entity", value: "legal_entity" },
    { label: "Individual", value: "individual" },
  ]

  return (
    <Layout title={"Management"} subtitle={renderNewRegisterButton()}>
      <Content>
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleFilter(values)}
          innerRef={formik}
        >
          {(formikProps) => (
            <Form style={{ minHeight: showFilter ? '250px' : '' }}>
              {renderModalApproval()}
              <RowFilterIcon>
                <MultipleExport
                  showExport={showExport}
                  setShowExport={setShowExport}
                  onExportReport={(reportName) => {
                    setShowExport(false);
                    if (reportName === "persons") return handleExportPersons(1, location.search || undefined);
                    if (reportName === "sanctionsReport") return handleExportSanctionsReport();
                    if (reportName === "insurerInfo") return handleExportInsurerInfo();
                  }}
                />
                <FilterContainer
                  className={showFilter ? "filter-open" : "filter-close"}
                >
                  <Row>
                    <Col style={{ display: "flex", justifyContent: 'flex-end' }}>
                      <Icon
                        name="close"
                        className="icon-close"
                        onClick={() => setShowFilter(false)}
                      />
                    </Col>
                  </Row>

                  <RowFilterOrder>
                    <MobileLabel>ORDER</MobileLabel>
                    <div style={{ display: "flex", flexDirection: "column", padding: "10px" }}>
                      {showFilter &&
                        columns.map((column, index) => (
                          <Row key={index}>
                            <GridColumnTitle
                              onClick={() =>
                                column.value && handleSortList(column.value)
                              }
                              style={{
                                cursor: column.value ? "pointer" : "not-allowed",
                              }}
                              className="mb-2 mr-2"
                            >
                              {column.title}
                            </GridColumnTitle>
                            {column.value &&
                              sortBy.key === column.value &&
                              sortBy.direction === "asc" ? (
                              <Icon name={"arrowup"} />
                            ) : column.value &&
                              sortBy.key === column.value &&
                              sortBy.direction === "desc" ? (
                              <Icon name={"arrowdown"} />
                            ) : (
                              ""
                            )}
                          </Row>
                        ))
                      }
                    </div>
                  </RowFilterOrder>
                  <hr className="d-md-none mb-3" />
                  <FilterSearchContainer>
                    <Row>
                      <MobileLabel>FILTER</MobileLabel>
                    </Row>
                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.filter.shortName.length}
                            onChange={() => formik.current?.setFieldValue("filter.shortName", "")}
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="filter.shortName"
                          placeholder="Short Name"
                          component={InputField}
                          validation={false}
                          onPressEnter={() => handleFilter()}
                        />
                      </FilterColumn>
                    </RowFilter>
                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.filter.fullName.length}
                            onChange={() => formik.current?.setFieldValue("filter.fullName", "")}
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="filter.fullName"
                          placeholder="Full Name"
                          component={InputField}
                          validation={false}
                          onPressEnter={() => handleFilter()}
                        />
                      </FilterColumn>
                    </RowFilter>
                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.filter.hierarchy}
                            onChange={() => formik.current?.setFieldValue("filter.hierarchy")}
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="filter.hierarchy"
                          placeholder="Hierarchy"
                          component={MultiSelectField}
                          validation={false}
                          options={hierarchyOptions}
                          format={(value) => {
                            const selectedHierarchy = hierarchyOptions.filter(option => value.includes(option.value))
                            return selectedHierarchy.map(item => item.label).join(', ')
                          }}
                        />
                      </FilterColumn>
                    </RowFilter>
                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.filter.parent.length}
                            onChange={() => formik.current?.setFieldValue("filter.parent", "")}
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="filter.parent"
                          placeholder="Parent"
                          component={InputField}
                          validation={false}
                          onPressEnter={() => handleFilter()}
                        />
                      </FilterColumn>
                    </RowFilter>
                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.filter.holding.length}
                            onChange={() => formik.current?.setFieldValue("filter.holding", "")}
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="filter.holding"
                          placeholder="Holding"
                          component={InputField}
                          validation={false}
                          onPressEnter={() => handleFilter()}
                        />
                      </FilterColumn>
                    </RowFilter>
                  </FilterSearchContainer>

                  <Row
                    align="center"
                    className="apply-button-container"
                  >
                    <Col>
                      <Button
                        primary
                        type="button"
                        id="btnFilter"
                        style={{ textAlign: "center" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowFilter(false);
                          handleFilter();
                        }}
                      >
                        Apply
                      </Button>
                    </Col>
                  </Row>
                </FilterContainer>
                <TooltipCard>
                  <TooltipText>
                    <ColIcon>
                      <Icon
                        name="filter"
                        onClick={(e) => setShowFilter(!showFilter)}
                      />
                    </ColIcon>
                  </TooltipText>
                  <TooltipBox>
                    {tooltips[585]}
                  </TooltipBox>
                </TooltipCard>
                <TooltipCard>
                  <TooltipText>
                    <ColIcon>
                      <Icon name="filterCan" onClick={() => resetFilters()} />
                    </ColIcon>
                  </TooltipText>
                  <TooltipBox>
                    {tooltips[586]}
                  </TooltipBox>
                </TooltipCard>
              </RowFilterIcon>

              <div style={{ marginLeft: 15, marginRight: 15 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {columns.map((column, index) => (
                        <TableHeader
                          key={index}
                          minWidth={column.minWidth}
                          maxWidth={column.maxWidth}
                          value={column.key}
                        >
                          <GridColumnTitle
                            onClick={() =>
                              column.value && handleSortList(column.value)
                            }
                            style={{
                              cursor: column.value ? "pointer" : "not-allowed",
                              paddingRight: column.key == 'actions' ? '40px' :''
                            }}
                          >
                            {column.title}
                          </GridColumnTitle>
                          {column.value &&
                            sortBy.key === column.value &&
                            sortBy.direction === "asc" ? (
                            <Icon name={"arrowup"} />
                          ) : column.value &&
                            sortBy.key === column.value &&
                            sortBy.direction === "desc" ? (
                            <Icon name={"arrowdown"} />
                          ) : (
                            ""
                          )}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <FieldArray
                      name="management"
                      render={() =>
                        persons.map((item, index) => (
                          <TableRow key={index}>
                            <TableCollum>
                              <TooltipCard>
                                <InlineLabel>Short Name</InlineLabel>
                                <div style={{ display: 'flex' }}>
                                  <DialogIcon>
                                    {item.isDisabled
                                      ? <Icon name="user-disabled" />
                                      : <Icon name="user-able" />}
                                  </DialogIcon>
                                  <TooltipText>
                                    <DialogIcon pink={"exclamation"}>
                                      {item.warning ? "!" : ""}
                                    </DialogIcon>
                                  </TooltipText>
                                  <TooltipBox>
                                    {tooltips[593]}
                                  </TooltipBox>
                                  <Label fontSize={12}>
                                    <span style={{ color: Colors.GRAY[3] }}>
                                      {item.idPerson}&nbsp;
                                    </span>{" "}
                                    {item.firstName}
                                  </Label>
                                </div>
                              </TooltipCard>
                            </TableCollum>

                            <TableCollum>
                              <InlineLabel>Full Name</InlineLabel>
                              <Label wordWrap fontSize={12}>
                                {item.lastName}
                              </Label>
                            </TableCollum>

                            <TableCollum>
                              <InlineLabel>Hierarchy</InlineLabel>
                              <Label wordWrap fontSize={12}>
                                {item.hierarchy}
                              </Label>
                            </TableCollum>

                            <TableCollum>
                              <InlineLabel>Parent</InlineLabel>
                              <Label wordWrap fontSize={12}>
                                {item.parent}
                              </Label>
                            </TableCollum>

                            <TableCollum>
                              <InlineLabel>Holding</InlineLabel>
                              <Label wordWrap fontSize={12}>
                                {item.holding}
                              </Label>
                            </TableCollum>

                            <TableCollum
                              style={{ textAlign: "right", paddingRight: 10 }}
                            >
                              <InlineLabel>Action</InlineLabel>
                              <div style={{ display: 'flex', justifyContent:'flex-end' }}>
                                {!item.isDisabled ?
                                  <>
                                    <TooltipCard>
                                      <TooltipText>
                                        <Icon
                                          onClick={() =>
                                            history.push(
                                              `/management/person/${item.idPerson}`
                                            )
                                          }
                                          name="edit"
                                          style={{
                                            alignSelf: "left",
                                            cursor: "pointer",
                                          }}
                                        />
                                      </TooltipText>
                                      <TooltipBox>
                                        {tooltips[581]}
                                      </TooltipBox>
                                    </TooltipCard>
                                    {item.idPerson === 1 &&
                                      <>
                                        <Icon
                                          onClick={() => history.push(`/products/person/${item.idPerson}`)}
                                          name="products"
                                          style={{
                                            alignSelf: "right",
                                            cursor: "pointer",
                                          }}
                                        />
                                        <Icon
                                          onClick={() => history.push('/currency/import-rate')}
                                          name="currency-rate"
                                          style={{
                                            alignSelf: "right",
                                            cursor: "pointer",
                                          }}
                                        />
                                      </>
                                    }
                                    <TooltipCard>
                                      <TooltipText>
                                        <Icon
                                          onClick={() => getAffectedEnquiries(item.idPerson)}
                                          name="disable-user"
                                          style={{
                                            alignSelf: "right",
                                            cursor: "pointer",
                                          }}
                                        />
                                      </TooltipText>
                                      <TooltipBox>
                                        {tooltips[589]}
                                      </TooltipBox>
                                    </TooltipCard>
                                    {(item.hierarchy !== 'Individual' && !item.isGovernment) &&
                                      <TooltipCard>
                                        <TooltipText>
                                          <Icon
                                            onClick={() => history.push(`/product-plan/person/${item.idPerson}`)}
                                            name="plan"
                                            style={{
                                              alignSelf: "right",
                                              cursor: "pointer",
                                            }}
                                          />
                                        </TooltipText>
                                        <TooltipBox>
                                          {tooltips[590]}
                                        </TooltipBox>
                                      </TooltipCard>
                                    }
                                  </> : <></>}
                              </div>
                            </TableCollum>
                          </TableRow>
                        ))
                      }
                    />
                  </TableBody>
                </Table>
              </div>
            </Form>
          )}
        </Formik>
        <LoadContainer>
          {!persons || persons.length === 0 || currentPage === totalPages ? (
            <Button forth onClick={(e) => e.preventDefault()}>
              NO MORE ITEMS
            </Button>
          ) : (
            <Button forth onClick={() => setNextPage()}>
              +LOAD MORE ACCOUNTS
            </Button>
          )}
        </LoadContainer>
      </Content>
      {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
    </Layout>
  );
}